import { useState } from "react";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import Alert from "../../components/common/Alert";
import Modal from "../../components/common/Modal";
import PageHeader from "../../components/common/PageHeader";
import Pagination from "../../components/common/Pagination";
import Table from "../../components/common/Table";
import ActionButton from "../../components/common/ActionButton";
import useClientPagination from "../../hooks/useClientPagination";
import {
  useGetAllergensQuery,
  useCreateAllergenMutation,
  useUpdateAllergenMutation,
  useDeleteAllergenMutation,
} from "../../store/api";

const Allergens = () => {
  const { data, isLoading, isError, error } = useGetAllergensQuery();
  const [createAllergen, createState] = useCreateAllergenMutation();
  const [updateAllergen, updateState] = useUpdateAllergenMutation();
  const [deleteAllergen, deleteAllergenState] = useDeleteAllergenMutation();

  const [form, setForm] = useState({ name: "", code: "", description: "" });
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    allergenId: null,
    allergenName: "",
  });

  const allergens = Array.isArray(data) ? data : [];
  const {
    page,
    totalCount,
    pageSize,
    paginatedItems: paginatedAllergens,
    setPage,
    setPageSize,
  } = useClientPagination(allergens, { pageSize: 10 });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await updateAllergen({ allergenId: editingId, payload: form }).unwrap();
        setEditingId(null);
      } else {
        await createAllergen(form).unwrap();
      }
      setForm({ name: "", code: "", description: "" });
      setShowForm(false);
    } catch (err) {
      // error handled by UI
    }
  };

  const handleEdit = (allergen) => {
    setEditingId(allergen.allergenId || allergen.id);
    setForm({
      name: allergen.name || allergen.allergenName || "",
      code: allergen.code || "",
      description: allergen.description || "",
    });
    setShowForm(true);
  };

  const handleDelete = (allergen) => {
    setDeleteModal({
      isOpen: true,
      allergenId: allergen.allergenId || allergen.id,
      allergenName: allergen.name || allergen.allergenName || "this allergen",
    });
  };

  const confirmDelete = async () => {
    try {
      await deleteAllergen(deleteModal.allergenId).unwrap();
    } catch (err) {
      // error handled by UI
    } finally {
      setDeleteModal({
        isOpen: false,
        allergenId: null,
        allergenName: "",
      });
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setForm({ name: "", code: "", description: "" });
    setShowForm(false);
  };

  const columns = [
    { key: "name", label: "Name" },
    { key: "code", label: "Code" },
    // { key: "description", label: "Description" },
    { key: "actions", label: "Actions" },
  ];

  const rows = paginatedAllergens.map((allergen) => {
    const id = allergen.allergenId || allergen.id;
    return {
      id,
      name: allergen.name || allergen.allergenName,
      code: allergen.code || "-",
      description: allergen.description || "-",
      actions: (
        <div className="flex gap-3">
          <ActionButton
            label="Edit"
            icon="edit"
            onClick={() => handleEdit(allergen)}
          />
          <ActionButton
            label="Delete"
            icon="delete"
            tone="danger"
            onClick={() => handleDelete(allergen)}
          />
        </div>
      ),
    };
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Allergens"
        subtitle="Manage allergen information for menu items."
        actions={
          showForm ? (
            <Button size="sm" variant="secondary" onClick={handleCancel}>
              Back
            </Button>
          ) : (
            <Button size="sm" onClick={() => setShowForm(true)}>
              Add Allergen
            </Button>
          )
        }
      />
      {showForm && (
        <Card>
          <h2 className="mb-4 text-lg font-semibold text-slate-900">
            {editingId ? "Edit Allergen" : "Add Allergen"}
          </h2>
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="grid gap-4 md:grid-cols-2">
              <Input
                label="Name"
                name="name"
                placeholder="Peanuts"
                value={form.name}
                onChange={handleChange}
                required
              />
              <Input
                label="Code"
                name="code"
                placeholder="PEANUTS"
                value={form.code}
                onChange={handleChange}
                helper="Required unique code used by the backend"
                required
              />
              {/* <Input
                label="Description"
                name="description"
                placeholder="Contains peanuts or peanut derivatives"
                value={form.description}
                onChange={handleChange}
              /> */}
            </div>

            {(createState.isSuccess || updateState.isSuccess) && (
              <Alert tone="success">
                Allergen {editingId ? "updated" : "created"} successfully.
              </Alert>
            )}
            {(createState.isError || updateState.isError) && (
              <Alert tone="error">
                {createState.error?.data?.detail ||
                  updateState.error?.data?.detail ||
                  "Failed to save allergen."}
              </Alert>
            )}

            <div className="flex gap-3">
              <Button
                type="submit"
                disabled={createState.isLoading || updateState.isLoading}
              >
                {editingId ? "Update" : "Add"} Allergen
              </Button>
              <Button type="button" variant="secondary" onClick={handleCancel}>
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      {!showForm && (
        <Card className="p-0">
          <div className="border-b border-slate-100 px-6 py-4 text-sm font-semibold text-slate-700">
            All Allergens
          </div>
          <div className="px-6 py-4">
            {isLoading && (
              <p className="text-sm text-slate-500">Loading allergens...</p>
            )}
            {isError && (
              <Alert tone="error">
                {error?.data?.detail || "Failed to load allergens."}
              </Alert>
            )}
            {!isLoading && !isError && (
              <>
                <Table
                  columns={columns}
                  rows={rows}
                  emptyText="No allergens yet."
                />
                <Pagination
                  page={page}
                  pageSize={pageSize}
                  totalCount={totalCount}
                  isLoading={isLoading}
                  onPageChange={setPage}
                  onPageSizeChange={setPageSize}
                />
              </>
            )}
          </div>
        </Card>
      )}

      <Modal
        isOpen={deleteModal.isOpen}
        onClose={() =>
          setDeleteModal({
            isOpen: false,
            allergenId: null,
            allergenName: "",
          })
        }
        title="Delete Allergen"
        confirmText="Delete"
        onConfirm={confirmDelete}
        isLoading={deleteAllergenState.isLoading}
      >
        <p>
          Are you sure you want to delete{" "}
          <strong>{deleteModal.allergenName}</strong>?
        </p>
      </Modal>
    </div>
  );
};

export default Allergens;
